// =============================================================================
// GOUV CONFIG - Widget Gouvernance : Configuration Globale
// Créé le 22/02/2026
// Gestion des paramètres système (tenants, modules, variables)
// =============================================================================

import { useState, useEffect, useCallback } from "react";
import {
  Settings,
  Building2,
  Layers,
  Key,
  Globe,
  Shield,
  RefreshCw,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Copy,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronUp,
  Users,
  Database,
  Server,
  Info,
  Plus
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Switch } from "../../../components/ui/switch";
import { Input } from "../../../components/ui/input";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../../components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../components/ui/tooltip";
import { toast } from "sonner";
import AssistantEspaceTravail from "./AssistantEspaceTravail";
import AdministrationTenant from "./AdministrationTenant";

const API = process.env.REACT_APP_BACKEND_URL;

// Catalogue des hubs : chargé depuis /api/platform/hubs-catalog (E4 —
// migration depuis hardcode vers source canonique HubDefinition.HUBS).
// Le mapping icon string → composant Lucide reste côté frontend (les
// icônes sont des composants React, non sérialisables côté backend).
const HUB_ICON_MAP = {
  Users, Database, Layers, Server, Building2, Shield,
  // Aliases pour anciens hubs si l'icône envoyée n'est pas dans la liste —
  // fallback Layers utilisé si key absent.
};

export default function GouvConfig({ onRefreshStats, currentUser }) {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("tenants");
  const [tenants, setTenants] = useState([]);
  const [availableModules, setAvailableModules] = useState([]);  // E4 : DB-driven
  const [moduleEnCours, setModuleEnCours] = useState(null);      // clé tenant:hub:module en cours d'écriture
  const [assistantOuvert, setAssistantOuvert] = useState(false);
  const [tenantAModifier, setTenantAModifier] = useState(null);
  const [expandedTenant, setExpandedTenant] = useState(null);
  const [systemConfig, setSystemConfig] = useState({
    api_url: API,
    environment: 'production',
    version: '2.0.0'
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      // Charger les tenants + le catalogue de hubs en parallèle
      const [tenantsRes, hubsRes] = await Promise.all([
        fetch(`${API}/api/platform/tenants`, { headers }),
        fetch(`${API}/api/platform/hubs-catalog`, { headers }),
      ]);
      if (tenantsRes.ok) {
        const data = await tenantsRes.json();
        setTenants(Array.isArray(data) ? data : data.tenants || []);
      }
      if (hubsRes.ok) {
        const data = await hubsRes.json();
        // Résoudre l'icône string → composant Lucide. Fallback sur Layers
        // si l'icône envoyée par le backend n'est pas mappée côté front.
        const mapped = (data.hubs || []).map(h => ({
          ...h,
          icon: HUB_ICON_MAP[h.icon] || Layers,
        }));
        setAvailableModules(mapped);
      }
    } catch (err) {
      console.error("Erreur chargement config:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // 🔴 Mesure du 2026-09-03 : cette fonction visait `PATCH /api/admin/tenants/…`,
  // une route qui N'EXISTE PAS (aucun routeur monté sur `/api/admin` — le seul
  // « admin » du backend est `sales_admin_router`, préfixe `/api/sales-admin`).
  // Elle n'était de toute façon JAMAIS appelée : l'onglet n'affichait que des
  // badges de lecture seule. ⛔ Le code retour ne pouvait pas le révéler — une
  // route inexistante rend 401, le middleware répondant avant le routage.
  //
  // 🔑 Cible corrigée : `modules`, la SEULE autorité qui produise un effet
  // runtime (`is_module_enabled` — 9 gardes réelles : tef, climat, aides,
  // plan_financement, ged). ⛔ PAS `hubs_actifs`, décoratif de bout en bout
  // (`@require_hub` = 0 application réelle, ses 2 occurrences sont des
  // exemples en docstring).
  const toggleModule = async (tenantId, hubId, moduleId, enable) => {
    const cle = `${tenantId}:${hubId}:${moduleId}`;
    setModuleEnCours(cle);
    try {
      const token = localStorage.getItem('session_token');
      const action = enable ? 'enable' : 'disable';
      const res = await fetch(
        `${API}/api/platform/tenants/${tenantId}/modules/${hubId}/${moduleId}/${action}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          credentials: 'include'
        }
      );

      if (res.ok) {
        toast.success(`Module ${moduleId} ${enable ? 'activé' : 'désactivé'}`);
        loadData();
      } else {
        const detail = await res.json().catch(() => ({}));
        toast.error(detail?.detail || `Échec (HTTP ${res.status})`);
      }
    } catch (err) {
      toast.error("Erreur réseau");
    } finally {
      setModuleEnCours(null);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copié dans le presse-papier");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <Settings className="w-7 h-7 text-zinc-400" />
            Configuration Globale
          </h2>
          <p className="text-zinc-500 mt-1">Paramètres système et gestion des tenants</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={loadData}
          disabled={loading}
          className="border-zinc-700"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-zinc-900/50 border border-zinc-800 p-1">
          <TabsTrigger value="tenants" className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Tenants
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 ml-1">
              {tenants.length}
            </Badge>
          </TabsTrigger>
          <TabsTrigger value="modules" className="flex items-center gap-2">
            <Layers className="w-4 h-4" />
            Modules
          </TabsTrigger>
          <TabsTrigger value="system" className="flex items-center gap-2">
            <Server className="w-4 h-4" />
            Système
          </TabsTrigger>
        </TabsList>

        {/* Tab Tenants */}
        <TabsContent value="tenants" className="mt-4">
          {/* 🔑 Création AUTONOME — l'exploitant crée son espace lui-même.
              L'assistant APPELLE `POST /api/platform/workspaces`, qui appelle
              `creer_espace_de_travail()` — la SEULE implémentation, partagée
              avec le CLI. ⛔ Il ne réimplémente rien. */}
          <div className="flex justify-end mb-3">
            <button
              type="button"
              onClick={() => setAssistantOuvert(true)}
              aria-label="Créer un espace de travail"
              data-testid="bouton-creer-espace"
              className="px-4 py-2 rounded-lg text-sm flex items-center gap-2"
              style={{ background: "var(--oh-accent-solid)", color: "var(--oh-accent-contrast)" }}
            >
              <Plus className="w-4 h-4" /> Créer un espace de travail
            </button>
          </div>
          {assistantOuvert && (
            <AssistantEspaceTravail
              onFerme={() => setAssistantOuvert(false)}
              onCree={() => { setAssistantOuvert(false); loadData(); }}
            />
          )}
          {tenantAModifier && (
            <AdministrationTenant
              tenant={tenantAModifier}
              hubsCatalogue={availableModules}
              onFerme={() => setTenantAModifier(null)}
              onApplique={() => { setTenantAModifier(null); loadData(); }}
            />
          )}
          <div className="space-y-3">
            {tenants.length > 0 ? (
              tenants.map(tenant => (
                <div
                  key={tenant.id || tenant._id || tenant.slug}
                  className="bg-zinc-900/50 border border-zinc-800 rounded-lg overflow-hidden"
                  data-testid={`tenant-${tenant.slug}`}
                >
                  {/* Tenant Header */}
                  <div
                    className="p-4 flex items-center gap-4 cursor-pointer hover:bg-zinc-800/30 transition-colors"
                    onClick={() => setExpandedTenant(expandedTenant === tenant.slug ? null : tenant.slug)}
                  >
                    <div className="w-10 h-10 rounded-lg bg-zinc-800 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-zinc-400" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-white">{tenant.nom || tenant.name || tenant.slug}</span>
                        {tenant.actif !== false && (
                          <Badge variant="outline" className="text-xs border-emerald-500/30 text-emerald-400">
                            Actif
                          </Badge>
                        )}
                        {tenant.trial && (
                          <Badge variant="outline" className="text-xs border-amber-500/30 text-amber-400">
                            Trial
                          </Badge>
                        )}
                        {tenant.is_platform_owner && (
                          <Badge variant="outline" className="text-xs border-purple-500/30 text-purple-400">
                            Platform Owner
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-zinc-500">{tenant.slug}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {tenant.hubs_actifs && (
                        <Badge variant="outline" className="text-xs border-zinc-700">
                          {tenant.hubs_actifs.length} hubs
                        </Badge>
                      )}
                      {expandedTenant === tenant.slug ? (
                        <ChevronUp className="w-5 h-5 text-zinc-500" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-zinc-500" />
                      )}
                    </div>
                  </div>

                  {/* Tenant Details */}
                  {expandedTenant === tenant.slug && (
                    <div className="px-4 pb-4 pt-0 border-t border-zinc-800 bg-zinc-950/50">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                        <div>
                          <label className="text-xs text-zinc-500">ID</label>
                          <div className="flex items-center gap-2 mt-1">
                            <code className="text-sm text-zinc-300 bg-zinc-800 px-2 py-1 rounded truncate max-w-[200px]">
                              {tenant.id || tenant._id}
                            </code>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => copyToClipboard(tenant.id || tenant._id)}
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setTenantAModifier(tenant); }}
                            aria-label={"Modifier " + (tenant.nom || tenant.slug)}
                            data-testid={"bouton-modifier-" + tenant.slug}
                            className="mt-2 px-3 py-1 rounded-lg text-xs"
                            style={{ border: "0.5px solid var(--oh-accent)", color: "var(--oh-accent-text)", background: "transparent" }}
                          >
                            Modifier
                          </button>
                        </div>
                        <div>
                          <label className="text-xs text-zinc-500">Slug</label>
                          <div className="flex items-center gap-2 mt-1">
                            <code className="text-sm text-zinc-300 bg-zinc-800 px-2 py-1 rounded">
                              {tenant.slug}
                            </code>
                          </div>
                        </div>
                        {tenant.description && (
                          <div className="col-span-2">
                            <label className="text-xs text-zinc-500">Description</label>
                            <p className="text-sm text-zinc-300 mt-1">{tenant.description}</p>
                          </div>
                        )}
                        {tenant.identite?.siret && (
                          <div>
                            <label className="text-xs text-zinc-500">SIRET</label>
                            <p className="text-sm text-zinc-300 mt-1">{tenant.identite.siret}</p>
                          </div>
                        )}
                        {tenant.created_at && (
                          <div>
                            <label className="text-xs text-zinc-500">Créé le</label>
                            <p className="text-sm text-zinc-300 mt-1">
                              {new Date(tenant.created_at).toLocaleDateString('fr-FR')}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Modules — la SEULE autorité qui produise un effet runtime.
                          ⛔ `hubs_actifs` n'est plus affiché comme une gouvernance :
                          il est décoratif (aucune garde ne le lit). */}
                      <div className="mt-4">
                        <label
                          className="text-xs mb-2 block"
                          style={{ color: "var(--oh-text-muted)" }}
                        >
                          Modules — un module désactivé rend ses routes indisponibles
                        </label>
                        <div className="space-y-3">
                          {availableModules.map(hub => {
                            const modsDuTenant = (tenant.modules || {})[hub.id] || {};
                            const modules = hub.modules || [];
                            if (!modules.length) return null;
                            return (
                              <div key={hub.id}>
                                <div
                                  className="text-xs mb-1.5"
                                  style={{ color: "var(--oh-text-secondary)" }}
                                >
                                  {hub.name}
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  {modules.map(mod => {
                                    const actif = mod.required
                                      || Boolean(modsDuTenant[mod.id]?.enabled);
                                    const cle = `${tenant.id || tenant._id}:${hub.id}:${mod.id}`;
                                    const enCours = moduleEnCours === cle;
                                    const fond = actif
                                      ? "var(--oh-stat-green-bg)"
                                      : "var(--oh-bg-surface)";
                                    const texte = actif
                                      ? "var(--oh-stat-green-text)"
                                      : "var(--oh-text-secondary)";
                                    const bordure = actif
                                      ? "var(--oh-stat-green-border)"
                                      : "var(--oh-border)";
                                    return (
                                      <button
                                        key={mod.id}
                                        type="button"
                                        disabled={mod.required || enCours}
                                        aria-label={`${actif ? "Désactiver" : "Activer"} le module ${mod.name}`}
                                        data-testid={`module-toggle-${hub.id}-${mod.id}`}
                                        title={mod.required
                                          ? "Module requis — non désactivable"
                                          : `${actif ? "Désactiver" : "Activer"} ${mod.name}`}
                                        onClick={() => {
                                          if (mod.required) return;
                                          if (actif && !window.confirm(
                                            `Désactiver « ${mod.name} » pour ${tenant.nom || tenant.slug} ?\n\n`
                                            + `Ses routes deviendront indisponibles pour ce tenant.`
                                          )) return;
                                          toggleModule(
                                            tenant.id || tenant._id, hub.id, mod.id, !actif
                                          );
                                        }}
                                        className="flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm transition-opacity"
                                        style={{
                                          background: fond,
                                          color: texte,
                                          borderColor: bordure,
                                          opacity: enCours ? 0.5 : 1,
                                          cursor: mod.required ? "default" : "pointer",
                                        }}
                                      >
                                        {actif
                                          ? <CheckCircle2 className="w-3.5 h-3.5" />
                                          : <XCircle className="w-3.5 h-3.5" />}
                                        <span>{mod.name}</span>
                                        {mod.required && (
                                          <span
                                            className="text-[10px] uppercase"
                                            style={{ color: "var(--oh-text-muted)" }}
                                          >
                                            requis
                                          </span>
                                        )}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="text-center py-12 text-zinc-500">
                <Building2 className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>Aucun tenant configuré</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Tab Modules */}
        <TabsContent value="modules" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {availableModules.map(mod => {
              const Icon = mod.icon;
              // Compter combien de tenants ont ce hub actif
              const activeCount = tenants.filter(t =>
                t.hubs_actifs && t.hubs_actifs.includes(mod.id)
              ).length;

              return (
                <div
                  key={mod.id}
                  className="p-4 bg-zinc-900/50 border border-zinc-800 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-zinc-800 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-zinc-400" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-white">{mod.name}</span>
                        <Badge variant="outline" className="text-xs border-zinc-700">
                          {mod.id}
                        </Badge>
                      </div>
                      <p className="text-sm text-zinc-500">{mod.description}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-white">{activeCount}</div>
                      <p className="text-xs text-zinc-500">tenant(s)</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-6 p-4 bg-zinc-900/30 border border-zinc-800 rounded-lg">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-400 mt-0.5" />
              <div>
                <p className="text-sm text-zinc-300">Gestion des hubs</p>
                <p className="text-xs text-zinc-500 mt-1">
                  L'activation/désactivation des hubs par tenant se fait depuis la page de détail
                  du tenant. Les hubs contrôlent l'accès aux grandes fonctionnalités de la plateforme.
                </p>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Tab Système */}
        <TabsContent value="system" className="mt-4">
          <div className="space-y-4">
            {/* Informations système */}
            <div className="p-4 bg-zinc-900/50 border border-zinc-800 rounded-lg">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Server className="w-5 h-5 text-cyan-400" />
                Informations système
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-zinc-500">API URL</label>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="text-sm text-zinc-300 bg-zinc-800 px-2 py-1 rounded flex-1 truncate">
                      {systemConfig.api_url}
                    </code>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => copyToClipboard(systemConfig.api_url)}
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-zinc-500">Environnement</label>
                  <div className="mt-1">
                    <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                      {systemConfig.environment}
                    </Badge>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-zinc-500">Version</label>
                  <p className="text-sm text-zinc-300 mt-1">{systemConfig.version}</p>
                </div>
                <div>
                  <label className="text-xs text-zinc-500">Policy Engine</label>
                  <div className="mt-1">
                    <Badge variant="outline" className="text-xs border-amber-500/30 text-amber-400">
                      Transversal v2.0
                    </Badge>
                  </div>
                </div>
              </div>
            </div>

            {/* Variables d'environnement (simulées) */}
            <div className="p-4 bg-zinc-900/50 border border-zinc-800 rounded-lg">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Key className="w-5 h-5 text-amber-400" />
                Variables critiques
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    <span className="text-sm text-zinc-300">MONGO_URL</span>
                  </div>
                  <Badge variant="outline" className="text-xs border-emerald-500/30 text-emerald-400">
                    Configuré
                  </Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    <span className="text-sm text-zinc-300">DB_NAME</span>
                  </div>
                  <Badge variant="outline" className="text-xs border-emerald-500/30 text-emerald-400">
                    Configuré
                  </Badge>
                </div>
                <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    <span className="text-sm text-zinc-300">OPENAI_API_KEY</span>
                  </div>
                  <Badge variant="outline" className="text-xs border-emerald-500/30 text-emerald-400">
                    Configuré
                  </Badge>
                </div>
              </div>
            </div>

            {/* Actions système */}
            <div className="p-4 bg-zinc-900/50 border border-zinc-800 rounded-lg">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-red-400" />
                Actions système
              </h3>
              <div className="flex flex-wrap gap-3">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                        onClick={async () => {
                          const res = await fetch(`${API}/api/policy/cache/invalidate`, {
                            method: 'POST',
                            headers: localStorage.getItem('session_token')
                              ? { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
                              : {}
                          });
                          if (res.ok) {
                            toast.success("Cache Policy invalidé");
                          }
                        }}
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Invalider cache Policy
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Force le rechargement des règles depuis la DB</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-zinc-700"
                        onClick={async () => {
                          const res = await fetch(`${API}/api/policy/initialize`, {
                            method: 'POST',
                            headers: localStorage.getItem('session_token')
                              ? { Authorization: `Bearer ${localStorage.getItem('session_token')}` }
                              : {}
                          });
                          if (res.ok) {
                            const data = await res.json();
                            toast.success(`Règles: ${data.status} (${data.count} règles)`);
                          }
                        }}
                      >
                        <Database className="w-4 h-4 mr-2" />
                        Réinitialiser règles
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Synchronise les règles GROOT avec la DB</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
